import { useCallback, useEffect, useMemo, useState } from 'react'
import * as api from 'api/entityApi'
import { useToast } from 'components/common/Toast'
import type { WaChannelTemplate } from 'types/entity'
import { type EntityContact, EMPTY_CONTACT } from './ContactCheckboxes'

interface WhatsappFormProps {
  entityId: string
  entityType?: 'lead' | 'enquiry'
  contact?: EntityContact
  onSuccess?: (msg: string) => void
  onCancel?: () => void
}

const digits = (value: string) => value.replace(/\D/g, '')

/** Keeps the first occurrence of each number, comparing digits only. */
function dedupeByDigits(numbers: string[]): string[] {
  const seen = new Set<string>()
  const result: string[] = []
  for (const number of numbers) {
    const key = digits(number)
    if (!key || seen.has(key)) continue
    seen.add(key)
    result.push(number)
  }
  return result
}

/**
 * Port of the legacy WhatsApp action: registered entities get the template
 * send (IMS/Whatsapp/Template); unregistered ones get the associate-number
 * flow from jsWaEntityMangager.
 */
export function WhatsappForm({
  entityId,
  entityType = 'lead',
  contact = EMPTY_CONTACT,
  onSuccess,
}: WhatsappFormProps) {
  const toast = useToast()

  const [checking, setChecking] = useState<boolean>(true)
  const [checkError, setCheckError] = useState<string>('')
  const [channelProfileId, setChannelProfileId] = useState<string>('')

  // Registered flow.
  const [templates, setTemplates] = useState<WaChannelTemplate[]>([])
  const [templateIndex, setTemplateIndex] = useState<string>('')
  const [mobileNo, setMobileNo] = useState<string>('')
  const [remarks, setRemarks] = useState<string>('')
  const [submitting, setSubmitting] = useState<boolean>(false)

  // Associate flow.
  const [associatedNumbers, setAssociatedNumbers] = useState<string[]>([])
  const [associatingNo, setAssociatingNo] = useState<string>('')

  const contactNumbers = useMemo(
    () => [contact.mobileNo, contact.mobileNo1, contact.mobileNo2].filter((m) => m.trim() !== ''),
    [contact.mobileNo, contact.mobileNo1, contact.mobileNo2],
  )

  const checkRegistration = useCallback(async () => {
    setChecking(true)
    setCheckError('')
    try {
      const profileId = await api.fetchWaRegistration(entityId, entityType)
      setChannelProfileId(profileId)
      if (profileId) {
        const tpls = await api.fetchWaTemplates()
        setTemplates(tpls)
        // Default the target number to the registered profile number.
        const match = [contact.mobileNo, contact.mobileNo1, contact.mobileNo2].find(
          (m) => m.trim() !== '' && digits(m) === digits(profileId),
        )
        setMobileNo(match ?? profileId)
      } else {
        const rows = await api.fetchWaAssociatedNumbers(entityId, entityType)
        setAssociatedNumbers(rows.map((r) => r.MobileNo ?? '').filter(Boolean))
      }
    } catch (err) {
      setCheckError((err as Error).message || 'Failed to check WhatsApp registration.')
    } finally {
      setChecking(false)
    }
  }, [entityId, entityType, contact.mobileNo, contact.mobileNo1, contact.mobileNo2])

  useEffect(() => {
    void checkRegistration()
  }, [checkRegistration])

  const handleAssociate = async (candidate: string) => {
    setAssociatingNo(candidate)
    try {
      const res = await api.associateWaNumber({ entityId, entityType, mobileNo: candidate })
      if (res.message) toast.push('info', res.message)
      await checkRegistration()
    } catch (err) {
      toast.push('error', (err as Error).message || 'Failed to associate the number.')
    } finally {
      setAssociatingNo('')
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    const template = templateIndex === '' ? undefined : templates[Number(templateIndex)]
    if (!template) {
      toast.push('error', 'Please select a WhatsApp template.')
      return
    }
    if (!mobileNo.trim()) {
      toast.push('error', 'Please select a mobile number.')
      return
    }
    setSubmitting(true)
    try {
      const msg = await api.submitWhatsapp({ entityId, entityType, mobileNo, template, remarks })
      onSuccess?.(msg)
    } catch (err) {
      toast.push('error', (err as Error).message || 'Failed to send WhatsApp message.')
    } finally {
      setSubmitting(false)
    }
  }

  if (checking) return <div className="widget-loader">Checking WhatsApp registration…</div>

  if (checkError) {
    return (
      <div className="action-form">
        <div className="alert alert-error">{checkError}</div>
        <button type="button" className="btn" onClick={() => void checkRegistration()}>
          Retry
        </button>
      </div>
    )
  }

  // ---------------------------------------------------------- associate flow
  if (!channelProfileId) {
    // The API list and the record can carry the same number in different
    // formats (+91-98… vs +91 98…) — dedupe on digits, keep the first form.
    const candidates = dedupeByDigits([...associatedNumbers, ...contactNumbers])
    return (
      <div className="action-form">
        <div className="form-hint" style={{ marginBottom: 10 }}>
          This record is not associated with a WhatsApp channel yet. Associate one of its numbers
          to start sending template messages.
        </div>
        {candidates.length === 0 && (
          <div className="alert alert-error">No mobile numbers available to associate.</div>
        )}
        {candidates.map((number) => (
          <div
            key={number}
            className="form-group flex-row"
            style={{ display: 'flex', alignItems: 'center', gap: 8, justifyContent: 'space-between' }}
          >
            <span>{number}</span>
            <button
              type="button"
              className="btn pri-btn"
              disabled={associatingNo !== ''}
              onClick={() => void handleAssociate(number)}
            >
              {associatingNo === number ? 'Associating…' : 'Associate'}
            </button>
          </div>
        ))}
      </div>
    )
  }

  // ---------------------------------------------------------- template send
  const numberOptions = dedupeByDigits([...contactNumbers, channelProfileId])

  return (
    <div className="action-form">
      <div className="form-group">
        <label className="form-label" htmlFor="wa-template">
          WhatsApp Template <span className="req">*</span>
        </label>
        <select
          id="wa-template"
          className="form-control"
          value={templateIndex}
          onChange={(e) => setTemplateIndex(e.target.value)}
        >
          <option value="">-- Select Template --</option>
          {templates.map((t, index) => (
            <option key={`${t.Id ?? index}`} value={String(index)}>
              {t.TemplateName ?? `Template ${index + 1}`}
            </option>
          ))}
        </select>
      </div>

      <div className="form-group">
        <label className="form-label" htmlFor="wa-mobile">
          Mobile Number <span className="req">*</span>
        </label>
        <select
          id="wa-mobile"
          className="form-control"
          value={mobileNo}
          onChange={(e) => setMobileNo(e.target.value)}
        >
          {numberOptions.map((number) => (
            <option key={number} value={number}>
              {number}
            </option>
          ))}
        </select>
      </div>

      <div className="form-group">
        <label className="form-label" htmlFor="wa-remarks">
          Remarks
        </label>
        <textarea
          id="wa-remarks"
          rows={2}
          className="form-control"
          placeholder="Remarks (optional)"
          value={remarks}
          onChange={(e) => setRemarks(e.target.value)}
        />
      </div>

      <div className="action-form-footer">
        <button type="button" className="btn pri-btn" disabled={submitting} onClick={handleSubmit}>
          {submitting ? 'Sending…' : 'Send WhatsApp'}
        </button>
      </div>
    </div>
  )
}
